import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import CameraModal from 'src/features/camera/components/CameraModal';
import { cameraMessages } from 'src/features/camera/utils/messages';
import { identifierTypeTranslationMap } from 'src/features/eventLog/utils/constants';
import {
    GatewaySensorState,
    GatewayState,
    MonitoringGateway as MonitoringGatewayType,
} from 'src/features/monitoring/types';
import { LOOP_POSITION_TRANSLATIONS_MAP } from 'src/features/monitoring/utils/constants';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { eventTypeTranslationMap } from 'src/features/systems/utils/constants';
import { attachStreamToVideo, detachStreamFromVideo } from 'src/features/videoPlayer/utils/webrtc';
import Video from 'src/features/videoPlayer/Video';
import WebRTCVideoPlayer from 'src/features/videoPlayer/WebRTCVideoPlayer';
import { useGetVisitor } from 'src/features/visitor/api/useGetVisitor';
import { GatewayAction, GatewayActionState } from 'src/features/visitor/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    GeneralBlock,
    GeneralBlockHead,
    GeneralBlockHeader,
    GeneralBlockSubtitle,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { VideoSkeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { translate } from 'src/utils/translate';

/**
 * Ref setter shared by the inline and the modal <video>.
 *
 * Both render the same live stream, so an element that mounts after `ontrack` fired — the modal,
 * which opens seconds later — has to pick the stream up here, and an element that unmounts has to
 * let go of it: a detached <video> that keeps `srcObject` goes on decoding until GC, which is one
 * leaked decoder per modal open/close.
 */
const bindVideoElement = (
    slot: React.RefObject<HTMLVideoElement | null>,
    element: HTMLVideoElement | null,
    streamRef: React.RefObject<MediaStream | null>,
) => {
    slot.current = element;

    void attachStreamToVideo(element, streamRef.current)?.catch((error) => {
        console.warn('play error', error);
    });

    return () => {
        detachStreamFromVideo(element);

        if (slot.current === element) slot.current = null;
    };
};

type Props = {
    gateway: MonitoringGatewayType;
    isOpen: boolean;
    visitors?: {
        currentLicensePlate?: string;
        currentNormalizedLicensePlate?: string | null;
        pastVisitorId?: string;
    };
    isBusy?: boolean;
    /** Lifted out of `gateway` so the parent can patch it from the
     *  `gateway_state_changed` WS event without re-fetching the list. */
    currentState?: GatewayState | null;
    /** Per-loop states, lifted out of `gateway.sensors` for the same reason — the parent
     *  overlays the `gateway_sensor_state_changed` edges on top of the fetched values. */
    loops?: GatewaySensorState[];
    setGatewayAction: (v: GatewayActionState) => void;
};
export default function SingleMonitoringGateway({
    gateway,
    isOpen,
    isBusy,
    visitors,
    currentState,
    loops = [],
    setGatewayAction,
}: Props) {
    const ref = React.useRef<HTMLDivElement>(null);
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const videoRef2 = React.useRef<HTMLVideoElement>(null);
    const streamRef = React.useRef<MediaStream | null>(null);
    const { parkingId } = useParking();

    const accessGatewaysActions = useHasPermissions([PERMISSION.ACCESS_GATEWAYS_MONITORING_ACTIONS]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const gatewayHasCamera = gateway.has_camera;

    const [selectedGatewayCamera, setSelectedGatewayCamera] = React.useState<string>('');
    const [clickedPlay, setClickedPlay] = React.useState<boolean>(false);
    const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<boolean>(false);

    const { formatMessage, locale } = useIntl();
    const { data } = useGetVisitor({
        parkingId,
        visitorId: visitors?.pastVisitorId || '',
        config: { enabled: !!visitors?.pastVisitorId },
    });

    const { translation, entryZone, exitZone } = React.useMemo(() => {
        const translation = translate(gateway.translations, locale);
        const entryZone = gateway.entry_zone ? translate(gateway.entry_zone.translations, locale)?.name : '-';
        const exitZone = gateway.exit_zone ? translate(gateway.exit_zone.translations, locale)?.name : '-';

        return {
            translation,
            entryZone,
            exitZone,
        };
    }, [gateway, locale]);

    React.useEffect(() => {
        if (!ref.current) return;

        if (isOpen) ref.current.style.height = String(ref.current.scrollHeight) + 'px';
        else ref.current.style.height = '0px';
    }, [isOpen]);

    const hiddenCameraStream = selectedGatewayCamera === gateway.id && isPlaying;
    const showPlayButton = !isPlaying && loading && !clickedPlay && !error;
    const showLoadingState = clickedPlay && loading && !isPlaying;

    const handleClicked = () => {
        setSelectedGatewayCamera(gateway.id);
        // Without this the retry path can never clear the overlay, so a recovered stream stays
        // buried under the error state for the lifetime of the card.
        setError(false);
        setClickedPlay((previous) => !previous);
    };

    const setVideoRef = React.useCallback<React.RefCallback<HTMLVideoElement>>(
        (element) => bindVideoElement(videoRef, element, streamRef),
        [],
    );
    const setVideoRef2 = React.useCallback<React.RefCallback<HTMLVideoElement>>(
        (element) => bindVideoElement(videoRef2, element, streamRef),
        [],
    );
    // Stable identity: the player tears the connection down whenever this array changes.
    const videoRefs = React.useMemo(() => [videoRef, videoRef2], []);

    const ButtonIcon = React.useCallback(() => {
        if (!gatewayHasCamera)
            return (
                <div className="flex justify-center">
                    <FontAwesomeIcon icon={ICON_MAP.CAMERA_SLASH} className={cn('absolute scale-200 text-white')} />
                    <div className="mt-6 text-xs text-red-700">{formatMessage(cameraMessages.cameraNotConfigured)}</div>
                </div>
            );
        if (error)
            return (
                <div className="flex justify-center">
                    <FontAwesomeIcon
                        icon={ICON_MAP.RETRY}
                        aria-hidden="true"
                        focusable="false"
                        className="absolute scale-200 text-white"
                    />
                    <div className="mt-6 text-xs text-red-700">
                        {formatMessage(cameraMessages.streamUnavailableError)}
                    </div>
                </div>
            );
        if (showLoadingState) return <VideoSkeleton />;
        if (showPlayButton)
            return (
                <FontAwesomeIcon
                    icon={ICON_MAP.PLAY}
                    aria-hidden="true"
                    focusable="false"
                    className={cn(
                        'absolute scale-200 text-white transition-transform group-hover:scale-250',
                        hiddenCameraStream && 'text-transparent group-hover:text-white',
                    )}
                />
            );

        return null;
    }, [gatewayHasCamera, formatMessage, error, showLoadingState, showPlayButton, hiddenCameraStream]);

    return (
        <div className="flex flex-col">
            {gatewayHasCamera && (
                <WebRTCVideoPlayer
                    startStream={clickedPlay}
                    streamRef={streamRef}
                    videoRefs={videoRefs}
                    selectedGateway={selectedGatewayCamera}
                    setIsPlaying={setIsPlaying}
                    setLoading={setLoading}
                    setError={setError}
                />
            )}
            <GeneralBlock className="flex-1 rounded-b-none border-b-0">
                <div className="flex w-full flex-row items-center justify-between">
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle className="text-base text-gray-900">
                                {translation?.name}
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="text-sm">
                                {translation?.description || '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <Badge theme={isBusy ? 'red' : 'green'}>
                        {formatMessage(isBusy ? parkingStatusMessages.busy : parkingStatusMessages.free)}
                    </Badge>
                </div>
                <div className="relative aspect-video w-full">
                    {/* Mounted only once the user starts the stream: the button below is opaque
                        until playback begins, so an always-mounted <video> would just animate a
                        hidden skeleton on every card in the grid. The loading state is drawn by
                        that button instead, which is why no `loading` is passed here. */}
                    {clickedPlay && <Video setVideoRef={setVideoRef} />}
                    <button
                        className={cn(
                            'group xs:aspect-auto bg-gray-300" absolute top-0 left-0 flex aspect-video h-full w-full items-center justify-center overflow-hidden rounded-xl bg-gray-200',
                            hiddenCameraStream && 'bg-transparent',
                            gatewayHasCamera && 'cursor-pointer',
                        )}
                        disabled={!gatewayHasCamera}
                        onClick={handleClicked}
                    >
                        <ButtonIcon />
                    </button>
                </div>
                {clickedPlay && (
                    <div className="relative">
                        <CameraModal
                            gateway={gateway}
                            isModalOpen={isModalOpen}
                            setIsModalOpen={setIsModalOpen}
                            isPlaying={isPlaying}
                        >
                            <Video loading={loading} setVideoRef={setVideoRef2} />
                        </CameraModal>
                    </div>
                )}
                <div
                    className={cn('flex flex-col gap-4 overflow-hidden duration-500 ease-in-out', !isOpen && '-mt-4')}
                    ref={ref}
                >
                    <div className="flex w-full justify-center gap-5 text-center text-xs">
                        <StatisticValue
                            title={formatMessage(parkingStatusMessages.totalPassesToday)}
                            value={gateway.total_passes_today}
                        />
                        <StatisticValue
                            title={formatMessage(parkingStatusMessages.failedPassesToday)}
                            value={gateway.failed_passes_today}
                        />
                    </div>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.entryZone} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>{entryZone}</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.exitZone} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>{exitZone}</GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.currentVisitor} />
                            </GeneralBlockTitle>
                            {/* The plate as the camera read it - what the stay is recorded under - and the
                                normalisation of that reading. They are the same value when it corrected
                                nothing, and each is named, so it is clear which is which */}
                            {visitors?.currentLicensePlate ? (
                                <GeneralBlockSubtitle className="flex flex-col">
                                    <span>
                                        <FormattedMessage {...parkingStatusMessages.standardLicensePlate} />:{' '}
                                        {visitors.currentLicensePlate}
                                    </span>
                                    <span>
                                        <FormattedMessage {...parkingStatusMessages.normalizedLicensePlate} />:{' '}
                                        {visitors.currentNormalizedLicensePlate ?? visitors.currentLicensePlate}
                                    </span>
                                </GeneralBlockSubtitle>
                            ) : (
                                <GeneralBlockSubtitle>-</GeneralBlockSubtitle>
                            )}
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.lastVisitor} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="flex gap-2">
                                {data ? (
                                    <>
                                        <LinkWithIcon
                                            to={`/${PATH.VISITORS}/visitor/${data.id}`}
                                            linkTitle={data?.main_identifier?.value ?? ''}
                                            hasPermissions={accessVisitors}
                                        />
                                        <Badge theme={visitorTypeBadgeOptions[data.type].theme}>
                                            <FontAwesomeIcon
                                                icon={visitorTypeBadgeOptions[data.type].icon || ICON_MAP.CLIENT}
                                            />
                                            {formatMessage(visitorTypeBadgeOptions[data.type].label)}
                                        </Badge>
                                    </>
                                ) : (
                                    '-'
                                )}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.requiredIdentifiers} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>
                                {gateway.required_identifier_types
                                    .map((type) =>
                                        formatMessage(
                                            identifierTypeTranslationMap[type] ?? {
                                                id: 'common.unknown',
                                                defaultMessage: type,
                                            },
                                        ),
                                    )
                                    .join(', ')}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.state} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle className="flex gap-2">
                                {currentState ? (
                                    <Badge theme={currentState === 'opened' ? 'green' : 'red'}>
                                        {formatMessage(
                                            currentState === 'opened'
                                                ? parkingStatusMessages.opened
                                                : parkingStatusMessages.closed,
                                        )}
                                    </Badge>
                                ) : (
                                    /* No communicator on this gateway reports barrier state, so we
                                       have nothing to show - not a closed barrier. */
                                    <Badge theme="gray">{formatMessage(parkingStatusMessages.notAvailable)}</Badge>
                                )}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                    {/* One attribute per loop, rendered like every other row. An empty list draws
                        nothing, so a gateway without loops grows no rows at all. */}
                    {loops.map((loop) => (
                        <GeneralBlockHeader key={loop.position}>
                            <GeneralBlockHead>
                                <GeneralBlockTitle>
                                    {formatMessage(
                                        LOOP_POSITION_TRANSLATIONS_MAP[loop.position] ?? {
                                            id: 'common.unknown',
                                            defaultMessage: loop.position,
                                        },
                                    )}
                                </GeneralBlockTitle>
                                <GeneralBlockSubtitle className="flex gap-2">
                                    <Badge
                                        /* Occupied reads red like the busy badge; a loop that has never
                                           reported is gray, not green - unknown is not clear. */
                                        theme={loop.activated === null ? 'gray' : loop.activated ? 'red' : 'green'}
                                    >
                                        {formatMessage(
                                            loop.activated === null
                                                ? parkingStatusMessages.notAvailable
                                                : loop.activated
                                                  ? parkingStatusMessages.loopActive
                                                  : parkingStatusMessages.loopInactive,
                                        )}
                                    </Badge>
                                </GeneralBlockSubtitle>
                            </GeneralBlockHead>
                        </GeneralBlockHeader>
                    ))}
                    <GeneralBlockHeader>
                        <GeneralBlockHead>
                            <GeneralBlockTitle>
                                <FormattedMessage {...parkingStatusMessages.message} />
                            </GeneralBlockTitle>
                            <GeneralBlockSubtitle>
                                {gateway.message
                                    ? formatMessage(
                                          eventTypeTranslationMap[gateway.message] ?? {
                                              id: 'common.unknown',
                                              defaultMessage: gateway.message,
                                          },
                                      )
                                    : '-'}
                            </GeneralBlockSubtitle>
                        </GeneralBlockHead>
                    </GeneralBlockHeader>
                </div>
            </GeneralBlock>

            {accessGatewaysActions && (
                <GeneralBlock className="flex w-full shrink flex-row gap-2 rounded-t-none">
                    <Button
                        className="flex-1"
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.OPEN,
                            })
                        }
                        size="sm"
                    >
                        <FontAwesomeIcon icon={ICON_MAP.ARROW_UP_RIGHT} />
                        <span className="xs:block hidden lg:block">
                            <FormattedMessage {...parkingStatusMessages.raise} />
                        </span>
                    </Button>
                    <Button
                        className="flex-1"
                        variant="destructiveLight"
                        disabled={!gateway.supports_emergency_open}
                        title={
                            gateway.supports_emergency_open
                                ? undefined
                                : formatMessage(parkingStatusMessages.emergencyOpenNotConfigured)
                        }
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.EMERGENCY,
                            })
                        }
                        size="sm"
                    >
                        <FontAwesomeIcon icon={ICON_MAP.DANGER} />
                        <span className="xs:block hidden lg:block">
                            <FormattedMessage {...parkingStatusMessages.emergencyRaise} />
                        </span>
                    </Button>
                    <Button
                        className="flex-1"
                        variant="destructiveLight"
                        disabled={!gateway.supports_emergency_open}
                        title={
                            gateway.supports_emergency_open
                                ? undefined
                                : formatMessage(parkingStatusMessages.emergencyOpenNotConfigured)
                        }
                        onClick={() =>
                            setGatewayAction({
                                gateway: { name: translation?.name || '', id: gateway.id },
                                action: GatewayAction.EMERGENCY_CLOSE,
                            })
                        }
                        size="sm"
                    >
                        <FontAwesomeIcon icon={ICON_MAP.DANGER} />
                        <span className="xs:block hidden lg:block">
                            <FormattedMessage {...parkingStatusMessages.emergencyClose} />
                        </span>
                    </Button>
                    {/*<Button*/}
                    {/*    className="flex-1"*/}
                    {/*    variant="outline"*/}
                    {/*    onClick={() =>*/}
                    {/*        setGatewayAction({*/}
                    {/*            gateway: { name: translation?.name || '', id: gateway.id },*/}
                    {/*            action: GatewayAction.LOCK,*/}
                    {/*        })*/}
                    {/*    }*/}
                    {/*    size="sm"*/}
                    {/*>*/}
                    {/*    <FontAwesomeIcon icon={ICON_MAP.PASSWORD} />*/}
                    {/*    <span className="xs:block hidden lg:block">*/}
                    {/*        <FormattedMessage {...parkingStatusMessages.lock} />*/}
                    {/*    </span>*/}
                    {/*</Button>*/}
                </GeneralBlock>
            )}
        </div>
    );
}
